import { Check, Minus } from 'lucide-react';
import type * as React from 'react';
import { cn } from './cn';

/**
 * The one tick-box.
 *
 * There were five. A bare `<input>` in the browser's own blue; `accent-accent`,
 * which tints the native box but keeps the platform's shape; `text-accent
 * focus:ring-accent/50`, written for a forms plugin this app has never had and
 * so doing nothing; `rounded-md` on a native box, which a native box ignores;
 * and a drawn span in the multi-select whose `rounded-md` — 8px, half its
 * width — turned a 16px square into a circle, so the vehicle picker
 * looked like a list of radio buttons for a control that takes many.
 *
 * Still a native input, so the keyboard, the form, `register()` and the screen
 * reader work without help. `appearance-none` hands the drawing to the classes
 * below, and the tick is an icon laid over it that follows the input's state
 * through `peer-checked` and `peer-indeterminate`.
 *
 * `indeterminate` is a DOM property, not an attribute, so it goes through the ref.
 */

const BOX = 'size-4 shrink-0 rounded-sm border transition-colors';
const BOX_OFF = 'border-input bg-background';
const BOX_ON = 'border-accent bg-accent text-accent-foreground';
const GLYPH = 'pointer-events-none absolute inset-0 m-auto size-3 text-accent-foreground';

export interface CheckboxProps extends Omit<React.ComponentProps<'input'>, 'type'> {
  /** Some but not all of what this box stands for is ticked. */
  indeterminate?: boolean;
}

export function Checkbox({ className, indeterminate = false, ref, ...props }: CheckboxProps) {
  const attach = (element: HTMLInputElement | null) => {
    if (element) element.indeterminate = indeterminate;
    if (typeof ref === 'function') ref(element);
    else if (ref) ref.current = element;
  };

  return (
    <span className={cn('relative inline-flex shrink-0', className)}>
      <input
        type="checkbox"
        ref={attach}
        className={cn(
          'peer cursor-pointer appearance-none',
          BOX,
          BOX_OFF,
          'checked:border-accent checked:bg-accent indeterminate:border-accent indeterminate:bg-accent',
          'outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 focus-visible:ring-offset-background',
          'disabled:cursor-not-allowed disabled:opacity-50'
        )}
        {...props}
      />
      <Check
        aria-hidden="true"
        strokeWidth={3}
        className={cn(GLYPH, 'hidden peer-checked:block peer-indeterminate:hidden')}
      />
      <Minus
        aria-hidden="true"
        strokeWidth={3}
        className={cn(GLYPH, 'hidden peer-indeterminate:block')}
      />
    </span>
  );
}

/**
 * The same box as a picture, for a row that is itself the button.
 *
 * A real input inside a `<button>` would be a second interactive element
 * nested in the first, reachable by Tab and announced twice; the row carries
 * `aria-pressed` and this only shows it.
 */
export function CheckboxMark({
  checked,
  indeterminate = false,
  className,
}: {
  checked: boolean;
  indeterminate?: boolean;
  className?: string;
}) {
  const isOn = checked || indeterminate;
  return (
    <span
      aria-hidden="true"
      className={cn(BOX, isOn ? BOX_ON : BOX_OFF, 'flex items-center justify-center', className)}
    >
      {indeterminate ? (
        <Minus strokeWidth={3} className="size-3" />
      ) : checked ? (
        <Check strokeWidth={3} className="size-3" />
      ) : null}
    </span>
  );
}
